import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { LocaleCode } from '@/api/models';
import type { ErrorResponse } from '@/api/handlers/responses';
import { useTrialBalanceQuery } from '@/api/queries';
import { PATH } from '@/router/PATH';
import { useBooksRange } from '../booksOutletContext';
import {
    formatGeneratedAt,
    isoToDisplayDate,
    resolveLocale,
    todayIso,
    unwrapStatement,
} from '@/pages/accounting/statements/statementMeta';
import {
    closingNoteKey,
    DEFAULT_CLOSING_VIEW,
    excludeClosingFor,
    ledgerRangeFor,
    toTrialBalanceRows,
    toTrialBalanceTotals,
    type ClosingView,
    type TrialBalanceRowView,
    type TrialBalanceTotalsView,
} from './trialBalanceMeta';

export type UseTrialBalancePageResult = {
    /**
     * The date the figures on screen are actually stated at, `DD/MM/YYYY`.
     *
     * Read off the loaded report, **not** off the requested date. The two
     * diverge while a new read is in flight: `keepPreviousData` holds the
     * previous report on screen (deliberately — no flash back to a skeleton on
     * every date change), so a heading written from the request would caption
     * those figures with a date they are not from.
     */
    asOfText: string;
    closingView: ClosingView;
    onClosingViewChange: (view: ClosingView) => void;
    /**
     * i18n key for the sentence stating what the chosen reading means. A key
     * rather than a string because the View owns every `t(...)` call.
     */
    closingNoteKey: string;
    locale: LocaleCode;
    isLoading: boolean;
    error: ErrorResponse | null;
    onRetry: () => void;
    /** Render-ready rows, or `null` until the first load resolves. */
    rows: TrialBalanceRowView[] | null;
    /** The totals row and the server's verdict, or `null` until then. */
    totals: TrialBalanceTotalsView | null;
    /**
     * The report loaded and holds no rows. Distinct from `rows === null`, which
     * means "not known yet" — see the note in the hook body.
     */
    isEmpty: boolean;
    /** Drill to the journal, filtered to one account over the report's window. */
    onViewLedger: (accountId: string) => void;
    /** `DD/MM/YYYY HH:mm` stamp shown in the footer. */
    generatedText: string;
};

/**
 * Drives the trial balance page.
 *
 * One request. Unlike the statements there is no advisory call to make: the
 * trial balance carries its own verdict in `balanced`, computed over the same
 * rows it sent.
 *
 * Nothing here sums or re-signs a figure. Both totals, the debit/credit
 * placement of every row and the balanced verdict all arrive computed — and the
 * one derivation that touches money, the difference between the totals, is only
 * ever *displayed* after the server has said the two disagree.
 *
 * The failure mode this hook is written against: **a page of zeros that balances
 * perfectly is the most convincing wrong answer this product can give.** So a
 * failed read is thrown to the boundary (there is no data to render), and a
 * successful read with no rows is reported as `isEmpty` — an explicit "nothing
 * had a balance on this date" — never a table of zeros under a green verdict.
 */
export const useTrialBalancePage = (): UseTrialBalancePageResult => {
    const { i18n } = useTranslation('pages');
    const locale = resolveLocale(i18n.language);
    const navigate = useNavigate();

    // The Books shell owns the period, so this page has no as-of control of its
    // own. Only the range **end** is meaningful: a trial balance states balances
    // *at a date*, cumulative from the start of the ledger, so the range start
    // has nothing to say to it. Today stands in when the page is rendered
    // outside the shell.
    const { toDate } = useBooksRange();
    const asOf = toDate || todayIso();
    const [closingView, setClosingView] =
        React.useState<ClosingView>(DEFAULT_CLOSING_VIEW);
    const generatedText = React.useRef(formatGeneratedAt(new Date())).current;

    const query = useTrialBalanceQuery(
        asOf,
        locale,
        excludeClosingFor(closingView),
    );
    const { data, error } = unwrapStatement(query.data, query.error);

    const rows = React.useMemo(
        () => (data ? toTrialBalanceRows(data.rows) : null),
        [data],
    );

    const totals = React.useMemo(
        () => (data ? toTrialBalanceTotals(data) : null),
        [data],
    );

    // `rows === null` is "not known yet"; `rows.length === 0` is "the ledger had
    // no balances on this date" — a real answer, and one the seeded tenant gives
    // for any date before its first posting. Only the second is an empty state.
    const isEmpty = rows !== null && rows.length === 0;

    // The heading describes the figures, so it comes from the report's own
    // `asOf` echo. Falling back to the requested date covers two cases: the
    // first load, where there is no report yet and nothing is rendered under the
    // heading anyway; and a server that sent no echo at all, where the requested
    // date is the best available answer and is at worst as wrong as it used
    // to be.
    const asOfText = isoToDisplayDate(data?.asOf ?? asOf);

    const onRetry = React.useCallback(() => {
        query.refetch();
    }, [query]);

    /**
     * Drill scoped to the window the figure was read from — ledger start through
     * the as-of date. A link that reaches the right account and reports "no
     * entries in this range" is worse than no link.
     */
    const onViewLedger = React.useCallback(
        (accountId: string) => {
            navigate(
                PATH.ACCOUNTING.BOOKS.BIND_JOURNAL_FOR_ACCOUNT(
                    accountId,
                    ledgerRangeFor(asOf),
                ),
            );
        },
        [navigate, asOf],
    );

    return {
        asOfText,
        closingView,
        onClosingViewChange: setClosingView,
        closingNoteKey: closingNoteKey(closingView),
        locale,
        isLoading: query.isLoading,
        error,
        onRetry,
        rows,
        totals,
        isEmpty,
        onViewLedger,
        generatedText,
    };
};
